import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, of, timer, Subscription } from 'rxjs';
import { shareReplay, tap, catchError, map } from 'rxjs/operators';

import { StateStorageService } from './state-storage.service';
import { Account } from '../model/account.model';
import { APP_ADMIN_SERVICE_API_URL, APP_USERS_SERVICE_API_URL, AUTH_SERVICE_API_URL } from '../../app.constant';
import { AuthService } from './auth.service';
import { GlobalService } from './global.service';

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  constructor(
    private http: HttpClient,
  ) { }

  getSubscribersList(): Observable<Array<any>> {
    return this.http.get<Array<any>>(APP_USERS_SERVICE_API_URL + '/get_subscribers_list');
  }

  getAddUsersData(): Observable<Array<any>> {
    return this.http.get<Array<any>>(APP_ADMIN_SERVICE_API_URL + '/get_add_users_data');
  }

applyFixesAndSave(postData: any): Observable<any> {
  return this.http.post<any>(`api/apply-fixes-and-save`, postData, { observe: 'response' });
}


  getValidationErrorsWithExcelStructure(postData: any): Observable<any> {
  const paginationData = postData.paginationData || {};
  const params = {
    currentPage: paginationData.currentPage?.toString() || '1',
    pageSize: paginationData.pageSize?.toString() || '100'
  };
  
  return this.http.get<any>(
    '/api/get-validation-errors-with-excel-structure',  // Relative URL
    { 
      params: params,
      withCredentials: true,  // This sends cookies
      observe: 'response' 
    }
  );
}

revalidateAndIngest(postData: { ingestion_id: number }): Observable<any> {
  return this.http.post<any>(
    '/api/revalidate-and-ingest',
    postData,
    { 
      observe: 'response',
      withCredentials: true
    }
  );
}



ignorePostIngestionError(postData: { fail_id: number }): Observable<any> {
  return this.http.post<any>(
    APP_USERS_SERVICE_API_URL + '/ignore-validation-error',
    postData,
    { observe: 'response' }
  );
}

fixCellValue(postData: { 
  fail_id: number; 
  column: string; 
  new_value: string 
}): Observable<any> {
  return this.http.post<any>(
    '/api/fix-cell-value',
    postData,
    { observe: 'response' }
  );
}

retryIngestion(postData: { ingestion_id: number }): Observable<any> {
  return this.http.post<any>(
    APP_USERS_SERVICE_API_URL + '/retry-ingestion',
    postData,
    { observe: 'response' }
  );
}

bulkUpsertValidData(postData: { 
  ingestion_id: number;
  skip_failed_rows: boolean 
}): Observable<any> {
  return this.http.post<any>(
    APP_USERS_SERVICE_API_URL + '/bulk-upsert-valid-data',
    postData,
    { observe: 'response' }
  );
}

  addUser(userData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/add_user', userData, { observe: 'response' });
  }

  UpdateUserRole(postData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/update_user_role', postData, { observe: 'response' });
  }

  resendUserInvitation(postData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/resend_user_invitation', postData, { observe: 'response' });
  }

  removeUser(postData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/remove_user', postData, { observe: 'response' });
  }

  getSavedDynamicQueryFilters(): Observable<Array<any>> {
    return this.http.get<Array<any>>(APP_ADMIN_SERVICE_API_URL + '/get_all_dynamic_query_filters');
  }

  saveDynamicQueryFilter(postData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/save_dynamic_query_filters', postData, { observe: 'response' });
  }

  editDynamicQueryFilter(postData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/edit_dynamic_query_filters', postData, { observe: 'response' });
  }

  deleteDynamicQueryFilter(postData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/delete_dynamic_query_filters', postData, { observe: 'response' });
  }

  createApiKey() {
    return this.http.get<any>(AUTH_SERVICE_API_URL + '/create_api_token', { observe: 'response' });
  }

  getSubcriberApiKeys() {
    return this.http.get<any>(APP_ADMIN_SERVICE_API_URL + '/get_subcriber_api_keys', { observe: 'response' });
  }

  getAliases(postBody: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/get_aliases', postBody, { observe: 'response' });
  }

  getAliasingMetadata() {
    return this.http.get<any>(APP_ADMIN_SERVICE_API_URL + '/get_aliasing_metadata', { observe: 'response' });
  }

  checkCoreObjectAttributeRelation(postBody: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/check_core_object_attribute_relation', postBody, { observe: 'response' });
  }

  getAliasGroups(postBody: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/get_alias_groups', postBody, { observe: 'response' });
  }

  saveAliasesAndRevisions(postData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/save_aliases_and_revisions', postData, { observe: 'response' });
  }

  getConfigurationIssueAlerts() {
    return this.http.get<any>(APP_ADMIN_SERVICE_API_URL + '/get_configuration_issue_alerts', { observe: 'response' });
  }

  getAllConfigurationIssues(postData: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/get_all_configuration_issues', postData, { observe: 'response' });
  }

  getConfigurationIssuesData(postBody: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/get_configuration_issues_data', postBody, { observe: 'response' });
  }

  ignoreConfigurationIssue(postBody: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/ignore_configuration_issue', postBody, { observe: 'response' });
  }

  getObjectsMetadataCreateDim() {
    return this.http.get<any>(APP_ADMIN_SERVICE_API_URL + '/get_objects_metadata_create_dim', { observe: 'response' });
  }

  getCreateDimMetadata(postBody: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/get_create_dim_metadata', postBody, { observe: 'response' });
  }

  createDim(postBody: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/create_dim', postBody, { observe: 'response' });
  }

  getConfigurationIssueAliasingMetadata() {
    return this.http.get<any>(APP_ADMIN_SERVICE_API_URL + '/get_configuration_issue_aliasing_metadata', { observe: 'response'  })
  }

  saveConfigurationIssueAlias(postBody: any) {
    return this.http.post<any>(APP_ADMIN_SERVICE_API_URL + '/save_configuration_issue_alias', postBody, { observe: 'response' });
  }

}
